import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useInView } from 'react-intersection-observer';
import { PlayCircleIcon, StopCircleIcon, QuestionMarkCircleIcon, SunIcon, SparklesIcon, HeartIcon, EyeIcon, FireIcon, DocumentTextIcon, LightBulbIcon, FaceSmileIcon, PrinterIcon, LinkIcon } from '@heroicons/react/24/solid'; // Iconos adicionales

// Importa tus audios
import startBellSound from '../../assets/audios/meditation/gongs/start_bell.mp3';
import stressReliefBgMusic from '../../assets/audios/meditation/bg/stress_relief_1h.MP3';

// Importa tu CustomToggle - CORREGIDA LA RUTA
import CustomToggle from '../CustomToggle'; // Asegúrate de que la ruta sea correcta

// Importa jspdf y html2canvas
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const LOCAL_STORAGE_KEY = '15MinutesNewBeliefSettings';

const FifteenMinutesNewBelief = () => {
  // Refs para las textareas auto-ajustables
  const newBeliefTextareaRef = useRef(null);
  const visionTextareaRef = useRef(null);
  const elevatedEmotionsTextareaRef = useRef(null);
  const shortStoryTextareaRef = useRef(null);
  const printContentRef = useRef(null); // Ref para el contenido a imprimir en PDF

  // --- Función para cargar configuraciones desde localStorage ---
  const loadSettings = useCallback(() => {
    try {
      const storedSettings = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (storedSettings) {
        return JSON.parse(storedSettings);
      }
    } catch (error) {
      console.error("Error al cargar configuraciones desde localStorage:", error);
    }
    return {}; // Retorna un objeto vacío si no hay configuraciones o hay un error
  }, []);

  // --- Estados de Configuración de la Meditación ---
  const initialSettings = loadSettings();

  const [presenceTime, setPresenceTime] = useState(initialSettings.presenceTime || 1);
  const [visualizationTime, setVisualizationTime] = useState(initialSettings.visualizationTime || 12);
  const [gratitudeTime, setGratitudeTime] = useState(initialSettings.gratitudeTime || 2);
  const [showBackgroundMusic, setShowBackgroundMusic] = useState(initialSettings.showBackgroundMusic !== undefined ? initialSettings.showBackgroundMusic : true);
  const [newBeliefText, setNewBeliefText] = useState(initialSettings.newBeliefText || '');
  const [visionText, setVisionText] = useState(initialSettings.visionText || '');
  const [elevatedEmotions, setElevatedEmotions] = useState(initialSettings.elevatedEmotions || '');
  const [shortStory, setShortStory] = useState(initialSettings.shortStory || '');
  const [error, setError] = useState('');

  // --- Estados del Flujo de la Meditación ---
  const [isMeditationActive, setIsMeditationActive] = useState(false);
  const [currentPhase, setCurrentPhase] = useState('initial'); // 'initial', 'presence', 'visualization', 'gratitude', 'complete'
  const [timeRemainingInPhase, setTimeRemainingInPhase] = useState(0); // Segundos
  const [totalTimeElapsed, setTotalTimeElapsed] = useState(0); // Segundos para el progreso total

  // --- Referencias para Audios y Timers ---
  const gongAudioRef = useRef(new Audio(startBellSound));
  const backgroundAudioRef = useRef(new Audio(stressReliefBgMusic));
  const timerIntervalRef = useRef(null);
  const totalTimerIntervalRef = useRef(null); // Inicializado con useRef

  // --- Animación con Intersection Observer ---
  const { ref: componentRef, inView: componentInView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  // --- Función para auto-ajustar la altura de una textarea ---
  const autoResizeTextarea = (textareaElement) => {
    if (textareaElement) {
      textareaElement.style.height = 'auto'; // Reset height
      textareaElement.style.height = textareaElement.scrollHeight + 'px'; // Set to scroll height
    }
  };

  // --- Efecto para auto-ajustar las textareas al cargar el componente y cuando los textos cambian ---
  useEffect(() => {
    autoResizeTextarea(newBeliefTextareaRef.current);
    autoResizeTextarea(visionTextareaRef.current);
    autoResizeTextarea(elevatedEmotionsTextareaRef.current);
    autoResizeTextarea(shortStoryTextareaRef.current);
  }, [newBeliefText, visionText, elevatedEmotions, shortStory]);


  // --- Efecto para guardar configuraciones en localStorage cada vez que cambian ---
  useEffect(() => {
    const settingsToSave = {
      presenceTime,
      visualizationTime,
      gratitudeTime,
      showBackgroundMusic,
      newBeliefText,
      visionText,
      elevatedEmotions,
      shortStory,
    };
    try {
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(settingsToSave));
    } catch (error) {
      console.error("Error al guardar configuraciones en localStorage:", error);
    }
  }, [presenceTime, visualizationTime, gratitudeTime, showBackgroundMusic, newBeliefText, visionText, elevatedEmotions, shortStory]);


  // --- Contenido de visualización por defecto si no hay inputs de usuario ---
  const defaultVisualizationContent = `Afirmación y Repetición: Repite tu nueva creencia en voz alta o en tu mente, con convicción. Siente cada palabra. No es una repetición mecánica, sino una declaración de tu nueva verdad.

Visualización Detallada: Cierra los ojos e imagínate viviendo desde esta nueva creencia. ¿Cómo te ves? ¿Qué estás haciendo? ¿Con quién estás? Si tu nueva creencia es sobre abundancia, visualiza tu cuenta bancaria, tus proyectos exitosos, las experiencias que ese dinero te permite vivir. Si es sobre una relación, visualiza las interacciones, las emociones que sientes. Hazlo lo más vívido y real posible.

Siente la Emoción: Este es el componente más crucial. No solo pienses en la creencia, siéntela. ¿Cómo se sentiría si esta creencia ya fuera tu verdad absoluta? Siente la alegría, la paz, la gratitud, la confianza, la expansión. Deja que esa emoción inunde cada célula de tu cuerpo. Sonríe, ríe si te apetece. Permítete experimentar esa realidad a nivel visceral. La emoción es el pegamento que une la nueva creencia a tu subconsciente.`;


  // --- Textos de las Fases ---
  const phaseTexts = {
    presence: {
      title: `Paso 1: Prepárate para el Enfoque (${presenceTime} minuto${presenceTime !== 1 ? 's' : ''})`,
      description: `Busca un lugar tranquilo donde no te interrumpan. Siéntate cómodamente o acuéstate. Cierra los ojos y respira profundamente unas cuantas veces, llevando tu atención a tu centro. Siente cómo te enraízas en el presente. Este es tu espacio sagrado para la creación.`
    },
    visualization: {
      title: `Paso 2: Sumérgete en la Nueva Creencia (${visualizationTime} minuto${visualizationTime !== 1 ? 's' : ''})`,
      description: `Durante los próximos ${visualizationTime} minutos, tu único objetivo es encarnar esta nueva creencia. `
      // La creencia, visión, emociones e historia se mostrarán en bloques separados, no concatenados aquí.
    },
    gratitude: {
      title: `Paso 3: Agradece y Suelta (${gratitudeTime} minuto${gratitudeTime !== 1 ? 's' : ''})`,
      description: `Al finalizar los ${visualizationTime} minutos, respira profundamente. Siente la gratitud por esta nueva verdad que estás instalando. Luego, viene la parte paradójica, pero esencial: suelta el resultado. Entregas tu intención al Universo, a tu Mente Superior, confiando plenamente en que se encargará de orquestar el "cómo" y el "cuándo" de la manera más elevada y armoniosa. No hay necesidad de forzarlo o de apegarse a un resultado o a una visión específica. La semilla ya fue plantada y lo único que queda es regarla. Cómo la riegas? Mantienes la emoción y el sentimiento sin apegarte a la visión y a los detalles.`
    },
    complete: {
      title: '¡Meditación Completada!',
      description: 'Has completado la meditación para instalar tu nueva creencia. Confía en el proceso y vive desde esta nueva verdad.'
    }
  };

  const totalMeditationDuration = (presenceTime + visualizationTime + gratitudeTime) * 60; // en segundos

  // --- Función para iniciar una fase específica ---
  const startPhase = useCallback((phase, duration) => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
    }
    gongAudioRef.current.play(); // Siempre se reproduce el gong al inicio de cada fase
    setCurrentPhase(phase);
    setTimeRemainingInPhase(duration * 60); // Convertir minutos a segundos

    timerIntervalRef.current = setInterval(() => {
      setTimeRemainingInPhase((prevTime) => {
        if (prevTime <= 1) {
          clearInterval(timerIntervalRef.current);
          // Transición a la siguiente fase
          if (phase === 'presence') {
            startPhase('visualization', visualizationTime);
          } else if (phase === 'visualization') {
            startPhase('gratitude', gratitudeTime);
          } else if (phase === 'gratitude') {
            // Gong final de la meditación
            gongAudioRef.current.play();
            setCurrentPhase('complete');
            setIsMeditationActive(false);
            if (backgroundAudioRef.current) {
                backgroundAudioRef.current.pause();
                backgroundAudioRef.current.currentTime = 0;
            }
            if (totalTimerIntervalRef.current) {
                clearInterval(totalTimerIntervalRef.current);
            }
          }
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);
  }, [presenceTime, visualizationTime, gratitudeTime]);


  // --- Manejador para iniciar la meditación ---
  const handleStartMeditation = () => {
    setError(''); // Limpiar cualquier error anterior
    setIsMeditationActive(true);
    setTotalTimeElapsed(0); // Resetear el contador de tiempo total

    // Iniciar música de fondo si está activada
    if (showBackgroundMusic && backgroundAudioRef.current) {
      backgroundAudioRef.current.loop = true;
      backgroundAudioRef.current.volume = 0.5; // Ajustar volumen inicial si es necesario
      backgroundAudioRef.current.play().catch(e => console.error("Error al reproducir música de fondo:", e));
    }

    // Iniciar el contador total
    totalTimerIntervalRef.current = setInterval(() => {
        setTotalTimeElapsed(prev => prev + 1);
    }, 1000);

    // Iniciar la primera fase
    startPhase('presence', presenceTime);
  };

  // --- Manejador para detener la meditación ---
  const handleStopMeditation = () => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
    }
    if (totalTimerIntervalRef.current) {
        clearInterval(totalTimerIntervalRef.current);
    }
    if (gongAudioRef.current) {
      gongAudioRef.current.pause();
      gongAudioRef.current.currentTime = 0;
    }
    if (backgroundAudioRef.current) {
      backgroundAudioRef.current.pause();
      backgroundAudioRef.current.currentTime = 0;
    }
    setIsMeditationActive(false);
    setCurrentPhase('initial'); // Vuelve al estado inicial para mostrar las settings
    setTimeRemainingInPhase(0);
    setTotalTimeElapsed(0);
  };

  // --- Manejador para imprimir el contenido a PDF usando html2canvas y jspdf ---
  const handlePrint = () => {
    if (printContentRef.current) {
      html2canvas(printContentRef.current, {
        scale: 2, // Aumenta la escala para mejor calidad en el PDF
        useCORS: true, // Importante si tienes imágenes o recursos externos
        scrollY: -window.scrollY, // Para asegurar que se capture la parte superior del elemento
        windowWidth: document.documentElement.offsetWidth,
        windowHeight: document.documentElement.offsetHeight
      }).then((canvas) => {
        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF('p', 'mm', 'a4'); // 'p' para portrait, 'mm' para milímetros, 'a4' para tamaño A4
        const imgWidth = 210; // Ancho A4 en mm
        const pageHeight = 297; // Alto A4 en mm
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        let heightLeft = imgHeight;

        let position = 0;

        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;

        while (heightLeft >= 0) {
          position = heightLeft - imgHeight;
          pdf.addPage();
          pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
          heightLeft -= pageHeight;
        }

        pdf.save('MiNuevaCreencia_Meditacion.pdf'); // Nombre del archivo
      }).catch(err => {
        console.error("Error al generar PDF:", err);
      });
    }
  };


  // --- Efecto para limpiar intervalos al desmontar el componente (existente) ---
  useEffect(() => {
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
      if (totalTimerIntervalRef.current) {
        clearInterval(totalTimerIntervalRef.current);
      }
      if (backgroundAudioRef.current) {
        backgroundAudioRef.current.pause();
        backgroundAudioRef.current.currentTime = 0;
      }
    };
  }, []);

  // --- Helper para formatear el tiempo ---
  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // --- Calcula el progreso de la meditación ---
  const meditationProgress = totalMeditationDuration > 0 ? (totalTimeElapsed / totalMeditationDuration) * 100 : 0;

  // --- Determinar si todos los campos de visualización están vacíos ---
  const areAllVisualizationInputsEmpty =
    !newBeliefText.trim() &&
    !visionText.trim() &&
    !elevatedEmotions.trim() &&
    !shortStory.trim();

  // YouTube video ID
  const youtubeVideoId = 'GcKO6M5R8zQ';
  const youtubeEmbedUrl = `https://www.youtube.com/embed/${youtubeVideoId}`;
  const youtubeWatchUrl = `https://www.youtube.com/watch?v=${youtubeVideoId}`;

  // Componente reutilizable para el video de YouTube
  const YoutubeVideoEmbed = () => (
    <div className="w-full max-w-xl mb-8 relative group" style={{ paddingTop: '56.25%' }}> {/* 16:9 Aspect Ratio */}
      <iframe
        className="absolute top-0 left-0 w-full h-full rounded-lg shadow-lg border border-gray-700"
        src={`${youtubeEmbedUrl}?autoplay=0&rel=0`}
        title="Meditación Guiada para una Nueva Creencia"
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      ></iframe>
      {/* Overlay transparente con icono de Play para hacer click y abrir en YouTube */}
      <a
        href={youtubeWatchUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="absolute inset-0 z-10 flex items-center justify-center cursor-pointer bg-black bg-opacity-20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-lg"
        aria-label="Abrir video en YouTube"
      >
        <PlayCircleIcon className="w-16 h-16 text-white" />
      </a>
    </div>
  );

  return (
    <div className="bg-black text-white min-h-screen font-sans flex flex-col items-center">
      <div className="absolute inset-0 bg-gradient-to-b from-gray-950 via-black to-gray-900 opacity-95"></div>

      <main className="relative z-10 flex-grow container mx-auto px-4 py-12 flex flex-col items-center">
        <div ref={componentRef}
          className={`w-full max-w-2xl mx-auto px-4 py-8 bg-gray-900 rounded-xl shadow-lg border border-gray-700
            transition-all duration-700 ease-in-out
            ${componentInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}
          `}
        >
          <div className="text-center mb-10">
            <LightBulbIcon className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
            <h1 className="text-3xl md:text-4xl font-bold text-white">15 Minutos para una Nueva Creencia</h1>
            <p className="text-gray-400 mt-2 max-w-md mx-auto">
              Instala una creencia poderosa en tu subconsciente a través de una meditación guiada.
            </p>
          </div>

          {error && <p className="text-red-400 text-center mb-4 text-sm">{error}</p>}

          {/* Renderizado condicional para asegurar que solo una sección principal se muestre a la vez */}
          {currentPhase === 'initial' && !isMeditationActive ? (
            // --- Formulario de Configuración Inicial ---
            <div className="flex flex-col gap-6">

              {/* Video de YouTube al principio de las settings */}
              <YoutubeVideoEmbed />

              {/* Textarea para la nueva creencia */}
              <div className="p-4 rounded-lg bg-gray-800/70 border border-gray-700">
                <label htmlFor="newBelief" className="block text-white font-bold mb-2">Tu Nueva Creencia</label>
                <textarea
                  id="newBelief"
                  ref={newBeliefTextareaRef}
                  value={newBeliefText}
                  onChange={(e) => {
                    setNewBeliefText(e.target.value);
                    autoResizeTextarea(e.target);
                  }}
                  placeholder="Ej: Soy merecedor de abundancia ilimitada."
                  rows="1"
                  className="w-full p-2.5 bg-gray-700 text-white rounded-lg border border-gray-600 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none overflow-hidden"
                ></textarea>
              </div>

              {/* Textarea para la visión */}
              <div className="p-4 rounded-lg bg-gray-800/70 border border-gray-700">
                <label htmlFor="visionText" className="block text-white font-bold mb-2">Tu Visión (¿Cómo se ve esta creencia manifestada?)</label>
                <div className="flex items-start gap-2">
                    <EyeIcon className="w-5 h-5 text-indigo-400 mt-2" />
                    <textarea
                        id="visionText"
                        ref={visionTextareaRef}
                        value={visionText}
                        onChange={(e) => {
                          setVisionText(e.target.value);
                          autoResizeTextarea(e.target);
                        }}
                        placeholder="Ej: Me veo trabajando en mi pasión, con ingresos estables y tiempo para mi familia."
                        rows="1"
                        className="w-full p-2.5 bg-gray-700 text-white rounded-lg border border-gray-600 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none overflow-hidden"
                    ></textarea>
                </div>
              </div>

              {/* Textarea para emociones elevadas */}
              <div className="p-4 rounded-lg bg-gray-800/70 border border-gray-700">
                <label htmlFor="elevatedEmotions" className="block text-white font-bold mb-2">Emociones Elevadas (¿Cómo te sentirías?)</label>
                <div className="flex items-start gap-2">
                    <FireIcon className="w-5 h-5 text-red-400 mt-2" />
                    <textarea
                        id="elevatedEmotions"
                        ref={elevatedEmotionsTextareaRef}
                        value={elevatedEmotions}
                        onChange={(e) => {
                          setElevatedEmotions(e.target.value);
                          autoResizeTextarea(e.target);
                        }}
                        placeholder="Ej: Alegría, gratitud, paz profunda, confianza inquebrantable, expansión."
                        rows="1"
                        className="w-full p-2.5 bg-gray-700 text-white rounded-lg border border-gray-600 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none overflow-hidden"
                    ></textarea>
                </div>
              </div>

              {/* Textarea para historia corta */}
              <div className="p-4 rounded-lg bg-gray-800/70 border border-gray-700">
                <label htmlFor="shortStory" className="block text-white font-bold mb-2">Historia Corta (Imagina un breve escenario)</label>
                <div className="flex items-start gap-2">
                    <DocumentTextIcon className="w-5 h-5 text-yellow-400 mt-2" />
                    <textarea
                        id="shortStory"
                        ref={shortStoryTextareaRef}
                        value={shortStory}
                        onChange={(e) => {
                          setShortStory(e.target.value);
                          autoResizeTextarea(e.target);
                        }}
                        placeholder="Ej: Me desperté hoy con una sonrisa, sabiendo que mi trabajo trae valor y que mis finanzas están en orden, permitiéndome vivir con libertad, viajar y apoyar a quienes amo. Cada día es una aventura de abundancia y conexión."
                        rows="4"
                        className="w-full p-2.5 bg-gray-700 text-white rounded-lg border border-gray-600 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none overflow-hidden"
                    ></textarea>
                </div>
              </div>

              {/* Secciones de Ajustes de Tiempo */}
              <div className="p-4 rounded-lg bg-gray-800/70 border border-gray-700">
                <h2 className="text-xl font-semibold text-yellow-400 mb-4">Duración de Fases</h2>
                <div className="space-y-4">
                  <div className="flex flex-col">
                    <label htmlFor="presenceTime" className="block text-gray-300 font-semibold mb-1">
                      <SunIcon className="w-5 h-5 inline-block mr-2 text-orange-300" /> Presencia
                    </label>
                    <div className="flex items-center gap-2 sm:gap-4 mt-2">
                        <input
                            type="range"
                            id="presenceTime"
                            min="1"
                            max="15"
                            step="1"
                            value={presenceTime}
                            onChange={(e) => setPresenceTime(parseInt(e.target.value, 10))}
                            className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-purple-500"
                        />
                        <span className="text-purple-300 font-mono w-16 text-right text-sm sm:text-base">{presenceTime} min</span>
                    </div>
                  </div>
                  <div className="flex flex-col">
                    <label htmlFor="visualizationTime" className="block text-gray-300 font-semibold mb-1">
                      <SparklesIcon className="w-5 h-5 inline-block mr-2 text-teal-300" /> Visualización
                    </label>
                    <div className="flex items-center gap-2 sm:gap-4 mt-2">
                        <input
                            type="range"
                            id="visualizationTime"
                            min="12"
                            max="45"
                            step="1"
                            value={visualizationTime}
                            onChange={(e) => setVisualizationTime(parseInt(e.target.value, 10))}
                            className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-purple-500"
                        />
                        <span className="text-purple-300 font-mono w-16 text-right text-sm sm:text-base">{visualizationTime} min</span>
                    </div>
                  </div>
                  <div className="flex flex-col">
                    <label htmlFor="gratitudeTime" className="block text-gray-300 font-semibold mb-1">
                      <HeartIcon className="w-5 h-5 inline-block mr-2 text-red-300" /> Agradecer y Soltar
                    </label>
                    <div className="flex items-center gap-2 sm:gap-4 mt-2">
                        <input
                            type="range"
                            id="gratitudeTime"
                            min="2"
                            max="15"
                            step="1"
                            value={gratitudeTime}
                            onChange={(e) => setGratitudeTime(parseInt(e.target.value, 10))}
                            className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-purple-500"
                        />
                        <span className="text-purple-300 font-mono w-16 text-right text-sm sm:text-base">{gratitudeTime} min</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Configuración de Música de Fondo usando CustomToggle */}
              <div className="p-4 rounded-lg bg-gray-800/70 border border-gray-700">
                <CustomToggle
                  id="backgroundMusic"
                  label="Música de Fondo"
                  isOn={showBackgroundMusic}
                  handleToggle={() => setShowBackgroundMusic(!showBackgroundMusic)}
                />
              </div>

              {/* Botón para iniciar meditación */}
              <button
                onClick={handleStartMeditation}
                className="w-full inline-flex items-center justify-center gap-3 px-8 py-3 bg-purple-600 text-white font-bold rounded-lg text-lg hover:bg-purple-700 transition-all transform hover:scale-105 disabled:bg-gray-600 disabled:cursor-not-allowed disabled:scale-100"
              >
                <PlayCircleIcon className="w-7 h-7"/>
                Iniciar Meditación
              </button>
            </div>
          ) : isMeditationActive ? (
            // --- Pantalla de Meditación Activa ---
            <div className="flex flex-col items-center text-center animate-fade-in-up">
              <div className="w-full bg-gray-700 rounded-full h-2.5 mb-4">
                <div
                  className="bg-gradient-to-r from-green-400 to-blue-500 h-2.5 rounded-full transition-all duration-1000 ease-linear"
                  style={{ width: `${meditationProgress}%` }}
                ></div>
              </div>
              <p className="text-xl text-gray-400 mb-2">
                Tiempo restante en fase: <span className="font-bold text-sky-400">{formatTime(timeRemainingInPhase)}</span>
              </p>
              <p className="text-xl text-gray-400 mb-6">
                Tiempo total transcurrido: <span className="font-bold text-teal-400">{formatTime(totalTimeElapsed)}</span>
              </p>


              {currentPhase === 'presence' && (
                <>
                  <h3 className="text-2xl font-bold text-purple-400 mb-4">{phaseTexts.presence.title}</h3>
                  {/* Texto de presencia en caja */}
                  <div className="w-full max-w-2xl text-left space-y-4">
                    <div className="p-3 bg-gray-800/70 rounded-lg border border-gray-700">
                        <p className="font-semibold text-lg text-orange-200 flex items-center gap-2"><SunIcon className="w-5 h-5"/> Instrucciones de Presencia:</p>
                        <p className="text-gray-300 ml-7 italic whitespace-pre-line">{phaseTexts.presence.description}</p>
                    </div>
                  </div>
                </>
              )}
              {currentPhase === 'visualization' && (
                <>
                  <h3 className="text-2xl font-bold text-sky-400 mb-4">{phaseTexts.visualization.title}</h3>
                  <p className="text-lg leading-relaxed text-gray-200 whitespace-pre-line text-left w-full max-w-2xl mb-6">
                    {phaseTexts.visualization.description}
                  </p>

                  <div className="w-full max-w-2xl text-left space-y-4">
                    {areAllVisualizationInputsEmpty ? (
                      <div className="p-3 bg-gray-800/70 rounded-lg border border-gray-700">
                         <p className="font-semibold text-lg text-yellow-200 flex items-center gap-2"><LightBulbIcon className="w-5 h-5"/> Contenido por Defecto:</p>
                         <p className="text-lg leading-relaxed text-gray-300 whitespace-pre-line ml-7 italic custom-scrollbar">
                           {defaultVisualizationContent}
                         </p>
                      </div>
                    ) : (
                      <>
                        {newBeliefText && (
                          <div className="p-3 bg-gray-800/70 rounded-lg border border-gray-700">
                            <p className="font-semibold text-lg text-purple-200 flex items-center gap-2"><LightBulbIcon className="w-5 h-5"/> Tu Creencia:</p>
                            <p className="text-gray-300 ml-7 italic whitespace-pre-line max-h-24 overflow-y-auto custom-scrollbar">"{newBeliefText}"</p>
                          </div>
                        )}
                        {visionText && (
                          <div className="p-3 bg-gray-800/70 rounded-lg border border-gray-700">
                            <p className="font-semibold text-lg text-indigo-200 flex items-center gap-2"><EyeIcon className="w-5 h-5"/> Tu Visión:</p>
                            <p className="text-gray-300 ml-7 italic whitespace-pre-line max-h-24 overflow-y-auto custom-scrollbar">"{visionText}"</p>
                          </div>
                        )}
                        {elevatedEmotions && (
                          <div className="p-3 bg-gray-800/70 rounded-lg border border-gray-700">
                            <p className="font-semibold text-lg text-red-200 flex items-center gap-2"><FaceSmileIcon className="w-5 h-5"/> Tus Emociones:</p> {/* MoodSmileIcon cambiado por FaceSmileIcon */}
                            <p className="text-gray-300 ml-7 italic whitespace-pre-line max-h-24 overflow-y-auto custom-scrollbar">"{elevatedEmotions}"</p>
                          </div>
                        )}
                        {shortStory && (
                          <div className="p-3 bg-gray-800/70 rounded-lg border border-gray-700">
                            <p className="font-semibold text-lg text-yellow-200 flex items-center gap-2"><DocumentTextIcon className="w-5 h-5"/> Tu Historia Corta:</p>
                            <p className="text-gray-300 ml-7 italic whitespace-pre-line max-h-24 overflow-y-auto custom-scrollbar">"{shortStory}"</p>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </>
              )}
              {currentPhase === 'gratitude' && (
                <>
                  <h3 className="text-2xl font-bold text-teal-400 mb-4">{phaseTexts.gratitude.title}</h3>
                  {/* Texto de gratitud en caja */}
                  <div className="w-full max-w-2xl text-left space-y-4">
                    <div className="p-3 bg-gray-800/70 rounded-lg border border-gray-700">
                        <p className="font-semibold text-lg text-red-200 flex items-center gap-2"><HeartIcon className="w-5 h-5"/> Instrucciones de Gratitud:</p>
                        <p className="text-gray-300 ml-7 italic whitespace-pre-line">{phaseTexts.gratitude.description}</p>
                    </div>
                  </div>
                </>
              )}

              <button
                onClick={handleStopMeditation}
                className="mt-8 py-2 px-5 bg-red-600 inline-flex items-center justify-center gap-2 text-white font-semibold rounded-lg shadow-md hover:bg-red-700 transition duration-300"
              >
                <StopCircleIcon className="w-6 h-6"/> Detener Meditación
              </button>
            </div>
          ) : currentPhase === 'complete' && !isMeditationActive ? (
            // --- Pantalla de Meditación Completada ---
            <div className="flex flex-col items-center text-center animate-fade-in-up">
              <h3 className="text-2xl font-bold text-green-400 mb-4">{phaseTexts.complete.title}</h3>
              <p className="text-gray-200 mb-6">{phaseTexts.complete.description}</p>

              {/* Video de YouTube al principio de la pantalla final */}
              <YoutubeVideoEmbed />

              {/* Contenido a imprimir en PDF */}
              <div ref={printContentRef} className="w-full p-4 bg-gray-800/70 rounded-lg border border-gray-700 text-left mb-6 space-y-4 max-w-2xl">
                <h4 className="text-xl font-bold text-purple-300 mb-4 text-center">Tu Configuración Meditativa:</h4>
                {areAllVisualizationInputsEmpty ? (
                   <div className="p-3 bg-gray-800/70 rounded-lg border border-gray-700">
                     <p className="text-lg leading-relaxed text-gray-300 whitespace-pre-line">
                       No ingresaste ninguna creencia, visión, emociones o historia para esta meditación. Puedes completar estos campos para una experiencia más personalizada.
                     </p>
                   </div>
                ) : (
                  <>
                    {newBeliefText && (
                      <div className="border-b border-gray-700/50 pb-2 last:border-b-0">
                        <p className="font-semibold text-lg text-white">Nueva Creencia:</p>
                        <p className="text-gray-300 ml-4 italic whitespace-pre-line">"{newBeliefText}"</p>
                      </div>
                    )}
                    {visionText && (
                      <div className="border-b border-gray-700/50 pb-2 last:border-b-0">
                        <p className="font-semibold text-lg text-white">Tu Visión:</p>
                        <p className="text-gray-300 ml-4 italic whitespace-pre-line">"{visionText}"</p>
                      </div>
                    )}
                    {elevatedEmotions && (
                      <div className="border-b border-gray-700/50 pb-2 last:border-b-0">
                        <p className="font-semibold text-lg text-white">Emociones Elevadas:</p>
                        <p className="text-gray-300 ml-4 italic whitespace-pre-line">"{elevatedEmotions}"</p>
                      </div>
                    )}
                    {shortStory && (
                      <div className="border-b border-gray-700/50 pb-2 last:border-b-0">
                        <p className="font-semibold text-lg text-white">Historia Corta:</p>
                        <p className="text-gray-300 ml-4 italic whitespace-pre-line">"{shortStory}"</p>
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="flex flex-col sm:flex-row gap-4 mt-4 w-full justify-center">
                <button
                  onClick={handlePrint}
                  className="py-3 px-6 bg-blue-600 inline-flex items-center justify-center gap-2 text-white font-semibold rounded-lg shadow-md hover:bg-blue-700 transition duration-300"
                >
                  <PrinterIcon className="w-6 h-6"/> Generar PDF
                </button>
                <button
                  onClick={handleStopMeditation}
                  className="py-3 px-6 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold rounded-lg shadow-md hover:from-purple-600 hover:to-pink-600 transition duration-300"
                >
                  Volver a Configuración
                </button>
                <a
                  href={youtubeWatchUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="py-3 px-6 bg-red-600 inline-flex items-center justify-center gap-2 text-white font-semibold rounded-lg shadow-md hover:bg-red-700 transition duration-300"
                >
                  <LinkIcon className="w-6 h-6"/> Abrir en YouTube
                </a>
              </div>
            </div>
          ) : null /* Fallback */}
        </div>
      </main>

      <footer className="relative z-10 text-center py-6 text-xs text-gray-600 mt-auto">
        © {new Date().getFullYear()} Gus • Aguiar | En misión de elevar y transformar.
      </footer>
    </div>
  );
};

export default FifteenMinutesNewBelief;